import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Collapse,
  IconButton,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  CircularProgress,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ViewListIcon from '@mui/icons-material/ViewList';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import { ConnectorDiagram } from './ConnectorDiagram';
import { useApi, configApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { usePermission } from '@backstage/plugin-permission-react';
import { superUserPermission } from '@internal/plugin-permissions-common-common';
import CodeIcon from '@mui/icons-material/Code';
import StorageIcon from '@mui/icons-material/Storage';
import SecurityIcon from '@mui/icons-material/Security';
import CloudIcon from '@mui/icons-material/Cloud';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import HubIcon from '@mui/icons-material/Hub';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import BuildIcon from '@mui/icons-material/Build';
import ArticleIcon from '@mui/icons-material/Article';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ConnectorAction {
  id: string;
  description: string;
  inputs: string[];
  outputs: string[];
}

interface ConnectorEndpoint {
  method: string;
  path: string;
  description: string;
}

interface ConnectorUsageStep {
  step: string;
  code?: string;
}

interface Connector {
  id: string;
  name: string;
  category: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  configKey?: string;
  envVars?: string[];
  actions?: ConnectorAction[];
  endpoints?: ConnectorEndpoint[];
  usedIn?: string[];
  usage?: ConnectorUsageStep[];
}

// ── Icon map (icons can't live in YAML) ──────────────────────────────────────

const ICON_MAP: Record<string, React.ReactNode> = {
  okta: <SecurityIcon />,
  bitbucket: <CodeIcon />,
  jira: <ArticleIcon />,
  confluence: <ArticleIcon />,
  awx: <AutoFixHighIcon />,
  n8n: <HubIcon />,
  skynet: <CloudIcon />,
  gocd: <BuildIcon />,
  'aws-bedrock': <SmartToyIcon />,
  postgres: <StorageIcon />,
};

function toConnector(c: any): Connector {
  return { ...c, icon: ICON_MAP[c.id] ?? <StorageIcon /> };
}

// ── Category colors ────────────────────────────────────────────────────────────

const CATEGORY_COLORS: Record<string, string> = {
  Auth: '#1a202c',
  'Source Control': '#2d3748',
  'Issue Tracking': '#2b6cb0',
  Docs: '#2c7a7b',
  Automation: '#c05621',
  Infrastructure: '#553c9a',
  AI: '#b7791f',
  'CI/CD': '#276749',
  Workflow: '#97266d',
};

// ── Styled components ──────────────────────────────────────────────────────────

const ConnectorCard = styled(Box)<{ expanded: boolean }>(({ expanded }) => ({
  border: '1px solid',
  borderColor: expanded ? '#1a202c' : 'rgba(0,0,0,0.1)',
  borderRadius: 12,
  overflow: 'visible',
  transition: 'all 0.2s ease',
  backgroundColor: 'white',
  boxShadow: expanded ? '0 4px 20px rgba(0,0,0,0.08)' : '0 1px 4px rgba(0,0,0,0.04)',
  '&:hover': {
    borderColor: '#1a202c',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  },
}));

const MethodBadge = styled(Box)<{ method: string }>(({ method }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 8px',
  borderRadius: 4,
  fontSize: '0.7rem',
  fontWeight: 700,
  fontFamily: 'monospace',
  backgroundColor:
    method === 'GET' ? '#e6f4ea' :
    method === 'POST' ? '#fff3e0' :
    method === 'ANY' ? '#f3e8ff' : '#f5f5f5',
  color:
    method === 'GET' ? '#1b5e20' :
    method === 'POST' ? '#e65100' :
    method === 'ANY' ? '#6b21a8' : '#424242',
}));

// ── Copy button helper ───────────────────────────────────────────────────────

const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);
  return (
    <Box
      component="span"
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      sx={{
        position: 'absolute', top: 6, right: 8,
        fontSize: '0.65rem', fontWeight: 600, color: copied ? '#4ade80' : '#9ca3af',
        cursor: 'pointer', userSelect: 'none',
        '&:hover': { color: copied ? '#4ade80' : '#e2e8f0' },
      }}
    >
      {copied ? 'Copied!' : 'Copy'}
    </Box>
  );
};

// ── ConnectorCard component ────────────────────────────────────────────────────

const ConnectorItem: React.FC<{ connector: Connector }> = ({ connector }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <ConnectorCard expanded={expanded}>
      {/* Header */}
      <Box
        sx={{ display: 'flex', alignItems: 'center', p: 2, cursor: 'pointer', gap: 2 }}
        onClick={() => setExpanded(e => !e)}
      >
        {/* Icon */}
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: `${connector.color}15`,
            border: `1px solid ${connector.color}30`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: connector.color,
            flexShrink: 0,
          }}
        >
          {connector.icon}
        </Box>

        {/* Name + category */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography fontWeight={700} fontSize="0.9rem" color="#1a202c">
              {connector.name}
            </Typography>
            <Chip
              label={connector.category}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.65rem',
                fontWeight: 700,
                bgcolor: `${CATEGORY_COLORS[connector.category]}15`,
                color: CATEGORY_COLORS[connector.category],
                border: `1px solid ${CATEGORY_COLORS[connector.category]}30`,
              }}
            />
          </Box>
          <Typography fontSize="0.78rem" color="text.secondary" noWrap>
            {connector.description.substring(0, 80)}...
          </Typography>
        </Box>

        <IconButton size="small" sx={{ color: '#718096', flexShrink: 0 }}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* Expanded content */}
      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ p: 2.5, bgcolor: '#fafafa', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          {/* Full description */}
          <Typography fontSize="0.82rem" color="#4a5568" lineHeight={1.7}>
            {connector.description}
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 2, width: '100%' }}>
            {/* Config key */}
            {connector.configKey && (
              <Box sx={{ minWidth: 0 }}>
                <Typography fontSize="0.7rem" fontWeight={700} color="#718096" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                  Config Key
                </Typography>
                <Box sx={{ fontFamily: 'monospace', fontSize: '0.78rem', bgcolor: '#1a202c', color: '#68d391', px: 1.5, py: 0.5, borderRadius: 1, wordBreak: 'break-all' }}>
                  {connector.configKey}
                </Box>
              </Box>
            )}

            {/* Env vars */}
            {connector.envVars && connector.envVars.length > 0 && (
              <Box sx={{ minWidth: 0 }}>
                <Typography fontSize="0.7rem" fontWeight={700} color="#718096" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                  Env Variables
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {connector.envVars.map(v => (
                    <Box key={v} sx={{ fontFamily: 'monospace', fontSize: '0.72rem', bgcolor: '#2d3748', color: '#fbd38d', px: 1, py: 0.3, borderRadius: 1, wordBreak: 'break-all' }}>
                      {v}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Used in */}
            {connector.usedIn && connector.usedIn.length > 0 && (
              <Box sx={{ minWidth: 0 }}>
                <Typography fontSize="0.7rem" fontWeight={700} color="#718096" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                  Used In
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {connector.usedIn.map(u => (
                    <Chip key={u} label={u} size="small" sx={{ height: 20, fontSize: '0.68rem', bgcolor: '#edf2f7', color: '#4a5568' }} />
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {/* Scaffolder Actions */}
          {connector.actions && connector.actions.length > 0 && (
            <Box sx={{ minWidth: 0 }}>
              <Typography fontSize="0.7rem" fontWeight={700} color="#718096" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
                Scaffolder Actions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {connector.actions.map(action => (
                  <Box key={action.id} sx={{ bgcolor: 'white', border: '1px solid #e2e8f0', borderRadius: 1.5, p: 1.5, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                      <Box sx={{ fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 700, color: '#1a202c', bgcolor: '#edf2f7', px: 1, py: 0.2, borderRadius: 0.5, wordBreak: 'break-all' }}>
                        {action.id}
                      </Box>
                    </Box>
                    <Typography fontSize="0.78rem" color="#4a5568" mb={0.75}>{action.description}</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                      <Box>
                        <Typography fontSize="0.65rem" fontWeight={700} color="#a0aec0" mb={0.3}>INPUTS</Typography>
                        {action.inputs.map(i => (
                          <Typography key={i} fontSize="0.72rem" color="#718096" sx={{ wordBreak: 'break-word', '&:before': { content: '"→ "', color: '#a0aec0' } }}>{i}</Typography>
                        ))}
                      </Box>
                      <Box>
                        <Typography fontSize="0.65rem" fontWeight={700} color="#a0aec0" mb={0.3}>OUTPUTS</Typography>
                        {action.outputs.map(o => (
                          <Typography key={o} fontSize="0.72rem" color="#718096" sx={{ wordBreak: 'break-word', '&:before': { content: '"← "', color: '#68d391' } }}>{o}</Typography>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* API Endpoints */}
          {connector.endpoints && connector.endpoints.length > 0 && (
            <Box sx={{ minWidth: 0 }}>
              <Typography fontSize="0.7rem" fontWeight={700} color="#718096" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1 }}>
                API Endpoints
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {connector.endpoints.map((ep, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, bgcolor: 'white', border: '1px solid #e2e8f0', borderRadius: 1, p: 1, minWidth: 0 }}>
                    <MethodBadge method={ep.method} sx={{ flexShrink: 0 }}>{ep.method}</MethodBadge>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography fontFamily="monospace" fontSize="0.75rem" color="#2d3748" fontWeight={600} sx={{ wordBreak: 'break-all' }}>{ep.path}</Typography>
                      <Typography fontSize="0.72rem" color="#718096">{ep.description}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* How to use in a plugin */}
          {connector.usage && connector.usage.length > 0 && (
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography fontSize="0.7rem" fontWeight={700} color="#718096" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  How to use in a plugin
                </Typography>
                <Box sx={{ height: '1px', flex: 1, bgcolor: '#e2e8f0' }} />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {connector.usage.map((u, i) => (
                  <Box key={i}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: u.code ? 0.75 : 0 }}>
                      <Box sx={{
                        width: 20, height: 20, borderRadius: '50%', bgcolor: connector.color,
                        color: 'white', fontSize: '0.65rem', fontWeight: 700,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: '1px',
                      }}>
                        {i + 1}
                      </Box>
                      <Typography fontSize="0.82rem" color="#374151" lineHeight={1.6}>{u.step}</Typography>
                    </Box>
                    {u.code && (
                      <Box sx={{ ml: 3.5, position: 'relative' }}>
                        <Box
                          component="pre"
                          sx={{
                            m: 0, p: 1.5,
                            bgcolor: '#1a202c',
                            borderRadius: 1.5,
                            fontSize: '0.72rem',
                            fontFamily: '"JetBrains Mono","Fira Code",monospace',
                            color: '#e2e8f0',
                            overflowX: 'auto',
                            lineHeight: 1.7,
                            whiteSpace: 'pre-wrap' as any,
                            wordBreak: 'break-all' as any,
                          }}
                        >
                          {u.code}
                        </Box>
                        <CopyButton text={u.code} />
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </ConnectorCard>
  );
};

// ── Main ConnectorsTab ─────────────────────────────────────────────────────────

const CATEGORIES = ['All', 'Auth', 'Source Control', 'Issue Tracking', 'Docs', 'Automation', 'Infrastructure', 'AI', 'CI/CD', 'Workflow'];

export const ConnectorsTab: React.FC = () => {
  const { allowed: isSuperUser } = usePermission({ permission: superUserPermission });
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState('All');
  const [view, setView] = useState<'list' | 'diagram'>('list');
  const [diagramTheme, setDiagramTheme] = useState<'dark' | 'light'>('light');

  const fetchConnectors = async () => {
    setFetchError(false);
    try {
      const baseUrl = configApi.getString('backend.baseUrl');
      const { token } = await identityApi.getCredentials();
      const res = await fetch(`${baseUrl}/api/custom-api/dev-hub/connectors`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json();
      const mapped = (data.connectors ?? []).map(toConnector);
      setConnectors(mapped);
      if (mapped.length > 0) setLastUpdated(data.connectors[0].updatedAt ?? null);
    } catch {
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setRefreshError(false);
    try {
      const baseUrl = configApi.getString('backend.baseUrl');
      const { token } = await identityApi.getCredentials();
      const res = await fetch(`${baseUrl}/api/custom-api/dev-hub/connectors/refresh`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      await fetchConnectors();
    } catch {
      setRefreshError(true);
    } finally {
      setRefreshing(false);
    }
  };

  React.useEffect(() => { fetchConnectors(); }, []);

  const filtered = activeCategory === 'All'
    ? connectors
    : connectors.filter(c => c.category === activeCategory);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ px: 3, py: 2, borderBottom: '1px solid rgba(0,0,0,0.08)', bgcolor: 'white', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: view === 'list' ? 1.5 : 0 }}>
          <Box>
            <Typography variant="h6" fontWeight={700} color="#1a202c" lineHeight={1.2}>
              Platform Connectors
            </Typography>
            <Typography variant="caption" color="text.secondary">
              All integrations and connectors wired into the COE Marketplace
              {lastUpdated && (
                <> &middot; Last updated {new Date(lastUpdated).toLocaleString()}</>
              )}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {isSuperUser && (
              <Tooltip title="Sync latest from git">
                <IconButton
                  size="small"
                  onClick={handleRefresh}
                  disabled={refreshing}
                  sx={{ color: '#718096', border: '1px solid #e2e8f0', borderRadius: '6px', p: 0.5 }}
                >
                  <RefreshIcon sx={{ fontSize: 18, animation: refreshing ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }} />
                </IconButton>
              </Tooltip>
            )}
            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={(_, v) => v && setView(v)}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  border: '1px solid #e2e8f0',
                  color: '#718096',
                  px: 1.5,
                  py: 0.5,
                  '&.Mui-selected': { bgcolor: '#1a202c', color: 'white', borderColor: '#1a202c' },
                  '&.Mui-selected:hover': { bgcolor: '#2d3748' },
                  '&:hover': { bgcolor: '#f7fafc' },
                },
              }}
            >
              <ToggleButton value="list"><ViewListIcon sx={{ fontSize: 18 }} /></ToggleButton>
              <ToggleButton value="diagram"><AccountTreeIcon sx={{ fontSize: 18 }} /></ToggleButton>
            </ToggleButtonGroup>
            {view === 'diagram' && (
              <Tooltip title={diagramTheme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}>
                <IconButton
                  size="small"
                  onClick={() => setDiagramTheme(t => t === 'dark' ? 'light' : 'dark')}
                  sx={{ color: '#718096', border: '1px solid #e2e8f0', borderRadius: '6px', p: 0.5 }}
                >
                  {diagramTheme === 'dark' ? <LightModeIcon sx={{ fontSize: 18 }} /> : <DarkModeIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Category filter + count — list view only */}
        {view === 'list' && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {CATEGORIES.map(cat => (
                <Chip
                  key={cat}
                  label={cat}
                  size="small"
                  onClick={() => setActiveCategory(cat)}
                  sx={{
                    height: 24,
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    bgcolor: activeCategory === cat ? '#1a202c' : '#f7fafc',
                    color: activeCategory === cat ? 'white' : '#4a5568',
                    border: '1px solid',
                    borderColor: activeCategory === cat ? '#1a202c' : '#e2e8f0',
                    '&:hover': { bgcolor: activeCategory === cat ? '#2d3748' : '#edf2f7' },
                    '& .MuiChip-label': { px: 1 },
                  }}
                />
              ))}
            </Box>
            <Typography fontSize="0.72rem" color="#9ca3af" fontWeight={500} sx={{ flexShrink: 0, ml: 2 }}>
              {filtered.length}{activeCategory !== 'All' ? ` of ${connectors.length}` : ''} connectors
            </Typography>
          </Box>
        )}
      </Box>

      {/* Content */}
      {refreshError && (
        <Box sx={{ mx: 3, mt: 1, px: 2, py: 1.5, bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <Typography sx={{ fontSize: '0.78rem', color: '#92400e' }}>Sync didn't go through — please try again.</Typography>
          <IconButton size="small" onClick={() => setRefreshError(false)} sx={{ color: '#92400e', p: 0.25 }}><CloseIcon sx={{ fontSize: 14 }} /></IconButton>
        </Box>
      )}
      {view === 'list' ? (
        <Box sx={{ flex: 1, overflowY: 'auto', p: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
              <CircularProgress size={28} sx={{ color: '#1a202c' }} />
            </Box>
          ) : fetchError ? (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
              <Typography sx={{ fontSize: '1.5rem' }}>🔌</Typography>
              <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#374151' }}>Couldn't load connectors</Typography>
              <Typography sx={{ fontSize: '0.78rem', color: '#9ca3af' }}>Something went wrong fetching the connectors.</Typography>
              <Button size="small" onClick={fetchConnectors} variant="outlined" sx={{ textTransform: 'none', mt: 0.5, borderColor: '#e2e8f0', color: '#4a5568', '&:hover': { borderColor: '#cbd5e0', bgcolor: '#f7fafc' } }}>Try again</Button>
            </Box>
          ) : filtered.map(connector => (
            <ConnectorItem key={connector.id} connector={connector} />
          ))}
        </Box>
      ) : (
        <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
          <Box sx={{ position: 'absolute', inset: 0 }}>
            <ConnectorDiagram connectors={connectors} diagramTheme={diagramTheme} />
          </Box>
        </Box>
      )}
    </Box>
  );
};
